import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Shield, CheckCircle2, XCircle, Clock, Search, Inbox, User as UserIcon, MailCheck, Crown, FileText, FolderLock } from "lucide-react";
import { toast } from "@/components/Toaster";

// Permission keys must mirror PERMISSION_KEYS in /app/backend/core/__init__.py.
// Each entry: { key, label, helper }.
const PERMS = [
  { key: "view",      label: "View",       helper: "Stream documents in the browser viewer" },
  { key: "fence",     label: "Fence view", helper: "Watermarked view-only with no download" },
  { key: "pdf",       label: "Download PDF", helper: "Download a converted PDF copy" },
  { key: "encrypted", label: "Encrypted PDF", helper: "Watermark-baked, copy/print disabled" },
  { key: "original",  label: "Download original", helper: "Original file (highest trust)" },
  { key: "upload",    label: "Upload",     helper: "Add new documents into the room" },
  { key: "manage",    label: "Manage",     helper: "Rename / move / permission edits" },
];

// Pre-defined templates so Owners can grant common roles in one click.
const TEMPLATES = [
  { key: "buyer",   label: "Buyer (read-only)", perms: { view: true, fence: true } },
  { key: "auditor", label: "Auditor (read + PDF)", perms: { view: true, fence: true, pdf: true } },
  { key: "seller",  label: "Seller-side (full)", perms: { view: true, fence: true, pdf: true, encrypted: true, original: true, upload: true, manage: true } },
];

export default function OwnerAccessRequests() {
  const [tab, setTab] = useState("pending"); // pending | approved | denied
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [approveTarget, setApproveTarget] = useState(null);
  const [denyTarget, setDenyTarget] = useState(null);
  const [rooms, setRooms] = useState([]);

  const load = async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/access-requests?status=${status}`);
      setRows(data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  useEffect(() => {
    api.get("/rooms").then(({ data }) => setRooms(data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.user_email, r.user_name, r.reason, r.room_name, r.room_name_hint]
        .some((s) => (s || "").toLowerCase().includes(needle)),
    );
  }, [rows, q]);

  const counts = useMemo(() => ({
    pending: rows.filter((r) => r.status === "pending").length,
    approved: rows.filter((r) => r.status === "approved").length,
    denied: rows.filter((r) => r.status === "denied").length,
  }), [rows]);

  return (
    <div className="p-8">
      <div className="label-mono text-muted-foreground flex items-center gap-2">
        <Shield className="w-3 h-3 text-emerald-500" /> Owner Console
      </div>
      <h1 className="mt-3 font-display text-4xl tracking-tighter font-medium">Access requests</h1>
      <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
        Users requesting room access land here. Approve with a permission template (or hand-pick perms),
        deny with an optional note, or keep the request for audit. Denied requests are kept indefinitely.
      </p>

      {/* Tabs */}
      <div className="mt-6 inline-flex border border-border rounded-md overflow-hidden">
        {[
          { k: "pending",  label: "Pending",  icon: Clock,        accent: "text-amber-500" },
          { k: "approved", label: "Approved", icon: CheckCircle2, accent: "text-emerald-500" },
          { k: "denied",   label: "Denied",   icon: XCircle,      accent: "text-rose-500" },
        ].map(({ k, label, icon: Icon, accent }) => (
          <button
            key={k}
            data-testid={`access-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm inline-flex items-center gap-2 transition ${
              tab === k ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
            }`}
          >
            <Icon className={`w-3.5 h-3.5 ${tab === k ? "" : accent}`} /> {label}
          </button>
        ))}
      </div>

      <div className="mt-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by requester, room, or reason…"
          className="w-full pl-10 pr-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid="access-search"
        />
      </div>

      {/* List */}
      <div className="mt-6 space-y-3">
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading requests…</div>
        ) : filtered.length === 0 ? (
          <EmptyState tab={tab} />
        ) : filtered.map((r) => (
          <RequestRow
            key={r.id} r={r} tab={tab}
            onApprove={() => setApproveTarget(r)}
            onDeny={() => setDenyTarget(r)}
          />
        ))}
      </div>

      {approveTarget && (
        <ApproveDialog
          req={approveTarget} rooms={rooms}
          onClose={() => setApproveTarget(null)}
          onDone={() => { setApproveTarget(null); load(tab); }}
        />
      )}
      {denyTarget && (
        <DenyDialog
          req={denyTarget}
          onClose={() => setDenyTarget(null)}
          onDone={() => { setDenyTarget(null); load(tab); }}
        />
      )}
    </div>
  );
}

function EmptyState({ tab }) {
  const copy = {
    pending: "No pending requests. When someone requests access, you'll see them here.",
    approved: "No approved requests yet.",
    denied: "No denied requests on record.",
  }[tab];
  return (
    <div className="border border-dashed border-border rounded-md py-12 text-center text-sm text-muted-foreground">
      <Inbox className="w-6 h-6 mx-auto mb-3 opacity-60" />
      {copy}
    </div>
  );
}

function RequestRow({ r, tab, onApprove, onDeny }) {
  return (
    <div data-testid={`access-row-${r.id}`} className="border border-border rounded-md p-4 hover:bg-secondary/40 transition">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm">
            <UserIcon className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="font-medium">{r.user_name}</span>
            <span className="text-muted-foreground">·</span>
            <span className="mono text-xs text-muted-foreground truncate">{r.user_email}</span>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            Requested {new Date(r.requested_at).toLocaleString()}
            {r.decided_at && tab !== "pending" && (
              <> · Decided {new Date(r.decided_at).toLocaleString()} by {r.decided_by_name}</>
            )}
          </div>
          <div className="mt-3 text-sm">
            {r.kind === "registration" ? (
              <span className="mono uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/30">
                New sign-up · pending Owner decision
              </span>
            ) : r.kind === "invite_accepted" ? (
              <span className="mono uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/30">
                Invite accepted · joined {r.room_name_hint || r.room_name || "data room"}
                {r.granted_role ? ` · ${r.granted_role}` : ""}
              </span>
            ) : (<>
              <span className="label-mono text-muted-foreground">Room: </span>
              {r.room_name ? (
                <span className="text-emerald-500">{r.room_name}</span>
              ) : r.room_name_hint ? (
                <span className="text-muted-foreground italic">{r.room_name_hint} (free-text)</span>
              ) : (
                <span className="text-muted-foreground italic">unspecified — you&apos;ll pick on approval</span>
              )}
            </>)}
          </div>
          {r.doc_id && r.doc_name && (
            <div className="mt-2 text-sm flex items-start gap-1.5">
              <span className="label-mono text-muted-foreground">Scoped to: </span>
              {r.doc_is_folder
                ? <FolderLock className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                : <FileText className="w-3.5 h-3.5 text-sky-500 mt-0.5 flex-shrink-0" />}
              <span className="font-medium truncate">{r.doc_name}</span>
              {Array.isArray(r.folder_path) && r.folder_path.length > 0 && (
                <span className="text-xs text-muted-foreground truncate">
                  · in {r.folder_path.map((f) => f.name).join(" / ")}
                </span>
              )}
            </div>
          )}
          <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
            <span className="label-mono">Current role:</span>
            {r.user_current_owner ? (
              <span className="inline-flex items-center gap-1 mono uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/30">
                <Shield className="w-2.5 h-2.5" /> Owner
              </span>
            ) : r.user_current_role === "admin" ? (
              <span className="inline-flex items-center gap-1 mono uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500 border border-amber-500/30">
                <Crown className="w-2.5 h-2.5" /> Admin
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 mono uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground border border-border">
                <UserIcon className="w-2.5 h-2.5" /> User
              </span>
            )}
          </div>
          <div className="mt-2 text-sm bg-secondary/30 border border-border rounded-md p-3 whitespace-pre-wrap">
            {r.reason}
          </div>
          {r.granted_perms && tab === "approved" && (
            <div className="mt-3 flex flex-wrap gap-1.5 items-center">
              {r.granted_role && (
                <span className="mono uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/30">
                  role → {r.granted_role}
                </span>
              )}
              {Object.entries(r.granted_perms).filter(([, v]) => v).map(([k]) => (
                <span key={k} className="mono uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/30">{k}</span>
              ))}
            </div>
          )}
          {r.deny_note && tab === "denied" && (
            <div className="mt-3 text-xs text-muted-foreground">
              <span className="label-mono">Note: </span>{r.deny_note}
            </div>
          )}
        </div>
        {tab === "pending" && (
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={onDeny}
              data-testid={`access-deny-${r.id}`}
              className="px-3 py-2 text-sm rounded-md border border-border hover:bg-destructive/10 hover:border-rose-500/40 hover:text-rose-500 transition inline-flex items-center gap-1.5"
            >
              <XCircle className="w-3.5 h-3.5" /> Deny
            </button>
            <button
              onClick={onApprove}
              data-testid={`access-approve-${r.id}`}
              className="px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition inline-flex items-center gap-1.5"
            >
              <MailCheck className="w-3.5 h-3.5" /> Approve
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ApproveDialog({ req, rooms, onClose, onDone }) {
  const [roomId, setRoomId] = useState(req.room_id || "");
  const [perms, setPerms] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Pre-fill role from the requester's CURRENT role so a "no-change" approval
  // doesn't accidentally lift/demote them. Owners must opt-in to a change.
  const initialRoleKey = req.user_current_owner ? "owner" : (req.user_current_role || "user");
  const [role, setRole] = useState(initialRoleKey);

  // Doc-scope: if the request was raised against a specific doc/folder, we
  // keep that scope by default. The Owner can untick to fall back to
  // room-level perms.
  const [scopeToDoc, setScopeToDoc] = useState(!!req.doc_id);

  const apply = (tplKey) => {
    const tpl = TEMPLATES.find((t) => t.key === tplKey);
    setPerms({ ...tpl.perms });
  };

  const toggle = (k) => setPerms((p) => ({ ...p, [k]: !p[k] }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      // Registration request: scope-less. Backend only consumes `role`
      // and clears the user's `pending_approval` flag.
      if (req.kind === "registration") {
        await api.post(`/access-requests/${req.id}/approve`, { role });
        toast.success(`Approved ${req.user_email} as ${role}`);
        onDone?.();
        return;
      }
      if (!roomId) { setErr("Pick a data room to grant access to."); return; }
      const body = { room_id: roomId, perms };
      // Only send `role` when the Owner actually changed it — keeps the
      // backend log clean and avoids unintended demotions on accidental clicks.
      if (role && role !== initialRoleKey) body.role = role;
      if (scopeToDoc && req.doc_id) body.doc_id = req.doc_id;
      await api.post(`/access-requests/${req.id}/approve`, body);
      toast.success(`Granted access to ${req.user_email}`);
      onDone?.();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-6 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-card border border-border rounded-md w-full max-w-lg p-6 relative my-8"
        data-testid="access-approve-dialog"
      >
        <h3 className="font-display text-xl tracking-tight">
          {req.kind === "registration" ? "Approve sign-up" : "Approve request"}
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          {req.kind === "registration"
            ? <>Pick the role for <span className="text-foreground font-medium">{req.user_email}</span>. They'll get a sign-in link by email.</>
            : <>Grant <span className="text-foreground font-medium">{req.user_email}</span> access to a room.</>}
        </p>

        <div className="mt-5 space-y-4">
          {req.kind === "registration" ? null : (<>
          {req.doc_id && req.doc_name && (
            <label className="flex items-start gap-3 p-3 rounded-md border border-emerald-500/30 bg-emerald-500/[0.06] cursor-pointer">
              <input
                type="checkbox" checked={scopeToDoc}
                onChange={() => setScopeToDoc((s) => !s)}
                data-testid="access-approve-scope-toggle"
                className="mt-0.5 accent-emerald-500"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium inline-flex items-center gap-1.5">
                  {req.doc_is_folder
                    ? <FolderLock className="w-3.5 h-3.5 text-amber-500" />
                    : <FileText className="w-3.5 h-3.5 text-sky-500" />}
                  Scope to this {req.doc_is_folder ? "folder" : "document"}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 truncate">{req.doc_name}</div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {scopeToDoc
                    ? "Perms apply ONLY to this item. The user gets room membership but can't see anything else."
                    : "Perms will apply room-wide instead."}
                </div>
              </div>
            </label>
          )}

          <div>
            <label className="label-mono">Data room</label>
            <select
              value={roomId} onChange={(e) => setRoomId(e.target.value)}
              data-testid="access-approve-room"
              className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              required
            >
              <option value="">Select a room…</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            {req.room_name_hint && (
              <p className="text-xs text-muted-foreground mt-1">Requester suggested: <span className="text-foreground">{req.room_name_hint}</span></p>
            )}
          </div>
          </>)}

          {/* Role tier picker — Owners can promote/demote at approve time.
              For registration requests this is the only control shown. */}
          <div>
            <label className="label-mono flex items-center justify-between">
              <span>Account role</span>
              {role !== initialRoleKey && (
                <span className="mono uppercase tracking-wider text-[10px] text-primary">
                  {initialRoleKey} → {role}
                </span>
              )}
            </label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {[
                { key: "user",  label: "User",  icon: UserIcon, helper: "View / interact with assigned rooms only",        accent: "text-muted-foreground" },
                { key: "admin", label: "Admin", icon: Crown,    helper: "Manage rooms, members, content & permissions",    accent: "text-amber-500" },
                { key: "owner", label: "Owner", icon: Shield,   helper: "Top-tier — also manages roles & access requests", accent: "text-emerald-500" },
              ].map((opt) => {
                const Icon = opt.icon;
                const active = role === opt.key;
                return (
                  <button
                    key={opt.key} type="button"
                    onClick={() => setRole(opt.key)}
                    data-testid={`access-role-${opt.key}`}
                    className={`px-2 py-2 rounded-md border text-xs transition flex flex-col items-center gap-1 ${
                      active
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border hover:bg-secondary"
                    }`}
                    title={opt.helper}
                  >
                    <Icon className={`w-4 h-4 ${active ? "" : opt.accent}`} />
                    <span className="font-medium">{opt.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              {role === "owner" && "Owner = full role-management + access-request approval rights."}
              {role === "admin" && "Admin = run rooms day-to-day; cannot change other users' roles."}
              {role === "user"  && "User = standard member; can only see rooms granted to them."}
            </p>
          </div>

          <div className={req.kind === "registration" ? "hidden" : ""}>
            <label className="label-mono">Quick templates</label>
            <div className="mt-1 flex flex-wrap gap-2">
              {TEMPLATES.map((t) => (
                <button
                  key={t.key} type="button" onClick={() => apply(t.key)}
                  data-testid={`access-tpl-${t.key}`}
                  className="px-3 py-1.5 text-xs rounded-md border border-border hover:bg-secondary transition"
                >{t.label}</button>
              ))}
            </div>
          </div>

          <div className={req.kind === "registration" ? "hidden" : ""}>
            <label className="label-mono">Permissions</label>
            <div className="mt-1 grid grid-cols-1 gap-1.5">
              {PERMS.map((p) => (
                <label key={p.key} className="flex items-start gap-3 px-3 py-2 rounded-md border border-border hover:bg-secondary/40 transition cursor-pointer">
                  <input
                    type="checkbox" checked={!!perms[p.key]}
                    onChange={() => toggle(p.key)}
                    data-testid={`access-perm-${p.key}`}
                    className="mt-1 accent-emerald-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{p.label}</div>
                    <div className="text-xs text-muted-foreground">{p.helper}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {err && <div className="mt-4 text-sm text-destructive">{err}</div>}

        <div className="mt-6 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-border hover:bg-secondary transition">Cancel</button>
          <button
            type="submit" disabled={busy}
            data-testid="access-approve-submit"
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> {busy ? "Granting…" : (req.kind === "registration" ? "Approve sign-up" : "Grant access")}
          </button>
        </div>
      </form>
    </div>
  );
}

function DenyDialog({ req, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post(`/access-requests/${req.id}/deny`, { note: note.trim() || null });
      toast.success(`Declined ${req.user_email}'s request`);
      onDone?.();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-card border border-border rounded-md w-full max-w-md p-6 relative"
        data-testid="access-deny-dialog"
      >
        <h3 className="font-display text-xl tracking-tight">Deny request</h3>
        <p className="text-xs text-muted-foreground mt-1">{req.user_email} will be notified.</p>

        <div className="mt-5">
          <label className="label-mono">Note (optional, shown to requester)</label>
          <textarea
            value={note} onChange={(e) => setNote(e.target.value)}
            rows={4}
            data-testid="access-deny-note"
            placeholder="e.g. We're at NDA stage; please re-request after sign-off"
            className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
        </div>

        {err && <div className="mt-4 text-sm text-destructive">{err}</div>}

        <div className="mt-6 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-border hover:bg-secondary transition">Cancel</button>
          <button
            type="submit" disabled={busy}
            data-testid="access-deny-submit"
            className="px-4 py-2 text-sm rounded-md bg-rose-500/10 border border-rose-500/40 text-rose-500 hover:bg-rose-500/15 transition disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <XCircle className="w-3.5 h-3.5" /> {busy ? "Declining…" : "Decline request"}
          </button>
        </div>
      </form>
    </div>
  );
}
